import React, { useState, useEffect, useContext } from "react";
import { Button, Col, Form, message, Modal, Row, Table } from "antd";
import * as AntIcons from "@ant-design/icons";
import dayjs from "dayjs";
import DynamicFormCase from "../DynamicFieldRenderer/DynamicFieldRenderer";
import "../../Components/DynamicForm/DynamicForm.css";
import { API } from "../../Axios/Axios";
import { LoaderContext } from "../../Helpers/Context/Context";
import { getCascadeFieldOptions } from "../../Helpers/APIEndpoints/Endpoints";

// ✅ Utility helpers (extracted to top-level)
const confirmDelete = (record: any, onConfirm: () => void) => {
  Modal.confirm({
    title: "Are you sure you want to delete this record?",
    okText: "Yes",
    okType: "danger",
    cancelText: "No",
    onOk: onConfirm,
  });
};

const uploadFileToServer = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("Files", file);
  try {
    const response = await API.post("/DynamicForm/GetFilePath", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data[0].value;
  } catch (error) {
    console.error("File upload failed:", error);
    return file.name;
  }
};

const fetchDropdownData = async (
  fieldId: string,
  execParams: any,
  showLoader: any,
  hideLoader: any,
): Promise<any[]> => {
  try {
    await showLoader();
    Object.keys(execParams).forEach((key: any) => {
      if (Array.isArray(execParams[key]))
        execParams[key] = execParams[key].join(",");
    });
    const response = await API.post(getCascadeFieldOptions, {
      FieldId: fieldId,
      FormData: JSON.stringify(execParams),
    });
    return response.data?.data ?? [];
  } catch (error) {
    console.error(`Error fetching data for ${fieldId}:`, error);
    return [];
  } finally {
    await hideLoader();
  }
};

const getCascadeOptions = async (
  currentField: any,
  data: any,
  showLoader: any,
  hideLoader: any,
): Promise<any[]> => {
  if (!currentField?.lazyload) return currentField.fieldOptions ?? [];

  const execParamsList: Record<string, any> = {};
  let hasError = false;
  (currentField.execParams ?? []).forEach((param: any) => {
    const value = data[param.name] ?? "";
    if (value === "") {
      hasError = true;
      execParamsList[param.name] = `${param.name} is required`;
    } else execParamsList[param.name] = value;
  });

  if (hasError) {
    return Object.values(execParamsList)
      .filter((msg) => (msg as string).includes("is required"))
      .map((msg) => ({ label: msg as string, value: "" }));
  }

  return await fetchDropdownData(
    currentField.Id,
    execParamsList,
    showLoader,
    hideLoader,
  );
};

const FormFieldSetComponent = ({
  fields = [],
  handleFieldSetButtonClick = () => {},
  actionType = "",
  dataSource = [],
  fieldSetKey = "",
}) => {
  const [form] = Form.useForm();
  const [gridColumns, setGridColumns] = useState<any[]>([]);
  const [isTempEdit, setIsTempEdit] = useState(false);
  const [targetIndex, setTargetIndex] = useState<number>();
  const [tableData, setTableData] = useState<any[]>([]);
  const { showLoader, hideLoader } = useContext(LoaderContext);
  const [tableRecord, setTableRecord] = useState<any>();

  // const handleDeleteClick = (record: any) => {
  //   const onConfirmDelete = () => {
  //     handleFieldSetButtonClick("Delete", fieldSetKey, null, record.Id);
  //     message.success("Record deleted successfully!");
  //   };

  //   confirmDelete(record, onConfirmDelete);
  // };

  debugger;
  const handleDeleteClick = (record: any) => {
    const onConfirmDelete = () => {
      // 1️⃣ Remove from local tableData immediately
      setTableData((prev) => {
        const updated = prev.filter((item) => item.Id !== record.Id);
        return updated;
      });

      handleFieldSetButtonClick("Delete", fieldSetKey, null, record.Id);

      message.success("Record deleted successfully!");
    };

    confirmDelete(record, onConfirmDelete);
  };

  // ✅ Build grid columns
  useEffect(() => {
    const columns = fields.map((sf: any) => ({
      key: sf.name,
      dataIndex: sf.name,
      title: sf.label,
      type: sf.type,
      fieldOptions: sf.fieldOptions,
    }));

    columns.push({
      key: "gridaction",
      dataIndex: "gridaction",
      title: "Action",
      width: "10%",
      render: (_text: any, record: any) => (
        <>
          <Button
            type="primary"
            className="me-3"
            icon={<AntIcons.EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="primary"
            icon={<AntIcons.DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
          />
        </>
      ),
    });
    // console.log("Setting grid columns:", columns);
    setGridColumns(columns);
  }, [fields]);

  const normalizeFormValues = (formValue: any) => {
    console.log("Raw form values before normalization:", formValue);
    Object.keys(formValue).forEach((key) => {
      if (Array.isArray(formValue[key])) {
        if (formValue[key].every((x) => typeof x === "string")) {
          formValue[key] = formValue[key].join(",");
        } else if (formValue[key].every((x) => x?.name)) {
          formValue[key] = formValue[key].map((f: any) => f.name).join(",");
        }
      } else if (formValue[key]?.$d) {
        formValue[key] = dayjs(formValue[key]).format("YYYY-MM-DD");
      }
    });

    return formValue;
  };

  // ✅ Handle Add/Update
  const handleAddUpdateRow = async (isEdit: boolean) => {
    console.log("Form values on submit:", form.getFieldsValue());
    await form.validateFields();
    const rawValues = { ...form.getFieldsValue(), CreatedBy: 1 };
    const cleanValues: Record<string, any> = {};

    for (const [key, value] of Object.entries(rawValues)) {
      if (Array.isArray(value)) {
        if (value[0]?.originFileObj) {
          const uploadedPaths = await Promise.all(
            value.map((item: any) => uploadFileToServer(item.originFileObj)),
          );
          cleanValues[key] = uploadedPaths.join(", ");
        } else {
          cleanValues[key] = value.join(", ");
        }
      } else if (value?.originFileObj) {
        cleanValues[key] = await uploadFileToServer(value.originFileObj);
      } else {
        cleanValues[key] = value;
      }
    }
    console.log(
      "Cleaned form values ready for submission:",
      normalizeFormValues(cleanValues),
      " fieldSetKey",
      fieldSetKey,
      " isEdit",
      isEdit,
      " targetIndex",
      targetIndex,
    );
    handleFieldSetButtonClick(
      isEdit ? "Update" : "Add",
      fieldSetKey,
      cleanValues,
      targetIndex,
    );
    if (isEdit) setIsTempEdit(false);
    form.resetFields();
  };

  const handleEdit = (record: any) => {
    setTargetIndex(record.Id);
    setIsTempEdit(true);
    setTableRecord({ ...record });
  };

  const handleCancel = () => {
    form.resetFields();
    setIsTempEdit(false);
  };

  // ✅ Update form fields when record selected for editing
  useEffect(() => {
    if (!tableRecord) return;
    (async () => {
      for (const field of fields) {
        const opts = await getCascadeOptions(
          field,
          tableRecord,
          showLoader,
          hideLoader,
        );
        field.fieldOptions = opts;
        if (field.type.toLowerCase() === "date") {
          tableRecord[field.name] = tableRecord[field.name]
            ? dayjs(tableRecord[field.name])
            : null;
        }
      }
      console.log("Updated tableRecord with cascade options:", tableRecord);
      form.setFieldsValue(tableRecord);
    })();
  }, [tableRecord]);

  // ✅ Table data sync
  useEffect(() => {
    if (dataSource?.length > 0) {
      // console.log("Data source updated:", dataSource, ' fields', form.getFieldValues());
      setTableData(dataSource ?? []);
    }
  }, [dataSource]);

  return (
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        {fields
          .filter((f: any) => f.parentId !== 0)
          .map((field: any) => (
            <DynamicFormCase
              key={field.name}
              field={field}
              fields={[]}
              form={form}
              keyIdentity=""
              actionType={actionType}
              dataSource={[]}
              colsize={undefined}
              section={undefined}
            />
          ))}
        <Col span={24}>
          <div className="button-section" style={{ textAlign: "center" }}>
            <Button className="btn-cancel" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              className="updateBtn"
              onClick={() => handleAddUpdateRow(isTempEdit)}
            >
              {isTempEdit ? "Update" : "Add"}
            </Button>
          </div>
        </Col>
        <Col span={24}>
          <Table
            bordered
            dataSource={tableData}
            columns={gridColumns}
            rowClassName="editable-row"
            style={{ width: "100%", marginBottom: "16px" }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default FormFieldSetComponent;
